import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { KeyValueFormulaFieldModel } from '@models/common';
import { FieldModel } from '@models/module/fields/main';
import { GridViewModel } from '@models/module/grid';
import { HttpService } from '@shared/services';

import {
  DeletedTableColumn,
  FieldSearchParams,
  ListElementModel,
  ListElementsRequest,
  SyncTableColumnsPayload,
  TableColumnModel,
} from './screen-designer.model';

export interface ExpressionField extends KeyValueFormulaFieldModel<string> {
  isNumberFormatting?: boolean;
}

@Injectable({ providedIn: 'root' })
export class FieldDefinitionsService {
  private readonly http = inject(HttpService);

  private moduleHeaders(moduleId: number) {
    return { moduleId: moduleId.toString() };
  }

  search(moduleId: number, search: FieldSearchParams): Observable<FieldModel[]> {
    const params = this.http.buildHttpParams(search);
    return this.http
      .post<
        GridViewModel<FieldModel[]>
      >('_api/FieldDefinition/Search', {}, { useLoader: false, httpOptions: { params, headers: this.moduleHeaders(moduleId) } })
      .pipe(map(result => result?.data ?? []));
  }

  get(moduleId: number, id: number): Observable<FieldModel> {
    const params = this.http.buildHttpParams({ id });
    return this.http.get<FieldModel>('_api/FieldDefinition/Get', {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  add(moduleId: number, body: Partial<FieldModel>, screenId?: number): Observable<number> {
    const params = this.http.buildHttpParams({ screenId });
    return this.http.post<number>('_api/FieldDefinition/Add', body, {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  edit(moduleId: number, body: Partial<FieldModel>) {
    return this.http.put('_api/FieldDefinition/Edit', body, {
      useLoader: false,
      httpOptions: { headers: this.moduleHeaders(moduleId) },
    });
  }

  delete(moduleId: number, id: number) {
    const params = this.http.buildHttpParams({ id });
    return this.http.put(
      '_api/FieldDefinition/Delete',
      {},
      {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      },
    );
  }

  getTableColumns(moduleId: number, tableId: number): Observable<TableColumnModel[]> {
    const params = this.http.buildHttpParams({ id: tableId });
    return this.http
      .get<TableColumnModel[]>('_api/FieldDefinition/TableFields', {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      })
      .pipe(map(columns => columns ?? []));
  }

  getDeletedTableColumns(moduleId: number, tableId: number): Observable<DeletedTableColumn[]> {
    const params = this.http.buildHttpParams({ id: tableId });
    return this.http
      .get<DeletedTableColumn[]>('_api/FieldDefinition/DeletedTableFields', {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      })
      .pipe(map(columns => columns ?? []));
  }

  syncTableColumns(moduleId: number, payload: SyncTableColumnsPayload) {
    const params = this.http.buildHttpParams({ id: payload.id });
    return this.http.put('_api/FieldDefinition/SyncTableFields', payload, {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  addTableColumn(moduleId: number, tableId: number, body: Partial<FieldModel>): Observable<number> {
    const params = this.http.buildHttpParams({ id: tableId });
    return this.http.post<number>('_api/FieldDefinition/AddTableField', body, {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  editTableColumn(moduleId: number, tableId: number, body: Partial<FieldModel>) {
    const params = this.http.buildHttpParams({ id: tableId });
    return this.http.put('_api/FieldDefinition/EditTableField', body, {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  deleteTableColumn(moduleId: number, id: number) {
    const params = this.http.buildHttpParams({ id });
    return this.http.put(
      '_api/FieldDefinition/DeleteTableField',
      {},
      {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      },
    );
  }

  getExpressionFields(moduleId: number, fieldId: number): Observable<ExpressionField[]> {
    const params = this.http.buildHttpParams({ fieldId });
    return this.http
      .get<ExpressionField[]>('_api/FieldDefinition/ExpressionDict', {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      })
      .pipe(map(fields => fields ?? []));
  }

  getListElements(moduleId: number, request: ListElementsRequest): Observable<ListElementModel[]> {
    return this.http
      .post<ListElementModel[]>('_api/ListsDefinition/GetElements', request, {
        useLoader: false,
        httpOptions: { headers: this.moduleHeaders(moduleId) },
      })
      .pipe(map(elements => elements ?? []));
  }
}
